import { useEffect, useState } from "preact/hooks";

export type ThemePref = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

export const THEME_KEY = "fa.theme";

export function readPref(): ThemePref {
  try {
    const v = localStorage.getItem(THEME_KEY);
    if (v === "light" || v === "dark" || v === "system") return v;
  } catch {
    /* ignore */
  }
  return "system";
}

export function writePref(p: ThemePref): void {
  try {
    localStorage.setItem(THEME_KEY, p);
  } catch {
    /* ignore */
  }
}

export function resolveSystem(): ResolvedTheme {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

export function resolve(pref: ThemePref): ResolvedTheme {
  return pref === "system" ? resolveSystem() : pref;
}

export function applyTheme(pref: ThemePref): void {
  document.documentElement.dataset.theme = resolve(pref);
}

export function useTheme(): {
  pref: ThemePref;
  setPref: (p: ThemePref) => void;
} {
  const [pref, setPrefState] = useState<ThemePref>(readPref);

  useEffect(() => {
    applyTheme(pref);
    if (pref !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => applyTheme("system");
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [pref]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== THEME_KEY) return;
      setPrefState(readPref());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  function setPref(p: ThemePref): void {
    writePref(p);
    setPrefState(p);
  }

  return { pref, setPref };
}
